import React, { useState } from 'react';
import { Button, Form, Input, Modal, Popover, Select, Switch, Table, Tabs, Tag, Tooltip } from 'antd';
import {
  SYSTEM_ROLE_SELECT_OPTIONS,
  getSystemRoleMeta,
  normalizeSystemRole,
  resolveDisplayRole
} from '../../utils/systemRoles';

/** 系统角色：直接显示英文值 admin / manager / captain / leader / user */
function renderSystemRole(userOrRole) {
  const role = typeof userOrRole === 'object' && userOrRole
    ? resolveDisplayRole(userOrRole)
    : normalizeSystemRole(userOrRole);
  const meta = getSystemRoleMeta(role);
  return <Tag color={meta.color}>{role}</Tag>;
}

/** 分组标签：与系统角色对应色区分 */
function renderGroupTags(groups) {
  if (!groups?.length) return '-';
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {groups.map((g) => {
        const name = g?.name || '';
        let color = 'blue';
        let tip = '权限分组';
        if (name === '管理员' || name === '系统管理员') {
          color = 'red';
          tip = 'role: admin';
        } else if (name === '管理员组') {
          color = 'orange';
          tip = 'role: manager';
        } else if (name === '队长') {
          color = 'geekblue';
          tip = 'role: captain';
        } else if (name === '组长') {
          color = 'purple';
          tip = 'role: leader';
        }
        return (
          <Tooltip key={g.id || name} title={tip}>
            <Tag color={color}>{name}</Tag>
          </Tooltip>
        );
      })}
    </span>
  );
}

const MEMBER_ICON_OPTIONS = [
  'water_drop', 'stationary', 'person', 'hike', 'walk', 'run', 'climb',
  'car', 'motorcycle', 'bicycle', 'boat', 'airplane',
  'helicopter', 'truck', 'bus', 'train', 'subway',
  'swim', 'ski', 'snowboard', 'surf', 'fish', 'hunt', 'camp',
  'fire', 'medical', 'police', 'ambulance', 'fire_truck', 'rescue',
  'flag', 'star', 'heart', 'diamond', 'circle', 'square', 'triangle'
];

/**
 * 组织/小组操作菜单：挂到 body，自绘菜单项，避免 antd Dropdown 继承标题栏白字变成「空白选项」。
 */
function OrgGroupActionPopover({ children, items }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      destroyOnHidden
      zIndex={2100}
      getPopupContainer={() => document.body}
      styles={{
        body: {
          padding: 4,
          background: '#fff',
          color: '#262626',
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
        }
      }}
      content={(
        <div
          style={{
            minWidth: 132,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            background: '#fff',
            color: '#262626'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((item) => {
            if (item.type === 'divider') {
              return (
                <div
                  key={item.key || 'divider'}
                  style={{ height: 1, margin: '4px 0', background: '#f0f0f0' }}
                />
              );
            }
            const danger = !!item.danger;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  padding: '6px 12px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: '22px',
                  color: danger ? '#cf1322' : '#262626',
                  fontWeight: 400
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = danger ? '#fff1f0' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    >
      {children}
    </Popover>
  );
}

/** 构建成员管理标签页，状态和业务操作由 AdminPanel 注入。 */
export function buildMembersTab(context) {
  const {
    shouldShowPage, userSearch, setUserSearch, handleAddUser, users, selectedUserKeys, setSelectedUserKeys,
    userPermissions, handleEditUser, handleDeleteUser, handleResetPwd, userModalVisible, setUserModalVisible,
    userForm, editingUser, handleSaveUser, memberSearch, setMemberSearch, setMemberModalVisible, fetchMembers,
    memberMsg, members, selectedMemberKeys, setSelectedMemberKeys, memberPagination, setMemberPagination,
    handleEditMember, handleDeleteMember, handleBatchDeleteMember, handleBatchDeleteLocation, handleExportTrack,
    openBatchIconModal, setExportModal, exportModal, batchIconModal, setBatchIconModal, submitBatchSetIcon,
    memberModalVisible, memberForm, handleSaveMember, memberEditModalVisible, setMemberEditModalVisible,
    memberEditForm, handleUpdateMember,
    organizations, orgInterconnect, handleToggleOrgInterconnect, memberOrgFilter, setMemberOrgFilter, memberGroupFilter, setMemberGroupFilter,
    handleAddOrg, handleRenameOrg, handleDeleteOrgModal, handleAddGroupOrg, handleRenameGroupOrg, handleDeleteGroupOrg
  } = context;

  const orgNames = Object.keys(organizations || {}).sort();
  const groupNamesForOrg = memberOrgFilter
    ? (organizations?.[memberOrgFilter] || [])
    : Array.from(new Set(Object.values(organizations || {}).flat())).sort();

  const tabs = [];
  if (shouldShowPage('用户管理') || shouldShowPage('会员信息管理')) {
    const memberTabs = [];

    if (shouldShowPage('用户管理')) {
      memberTabs.push({
        key: 'users',
        label: '系统账号',
        children: (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="搜索用户名/姓名/邮箱"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                style={{ width: 250, marginRight: 8 }}
              />
              <Button type="primary" onClick={handleAddUser}>添加用户</Button>
            </div>
            <Table
              dataSource={users.filter(u => !userSearch
                || u.username?.includes(userSearch)
                || u.real_name?.includes(userSearch)
                || u.email?.includes(userSearch))}
              rowKey={(record) => String(record.id)}
              rowSelection={{ selectedRowKeys: selectedUserKeys, onChange: setSelectedUserKeys }}
              columns={[
                { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
                { title: '用户名', dataIndex: 'username', key: 'username' },
                { title: '姓名', dataIndex: 'real_name', key: 'real_name' },
                { title: '邮箱', dataIndex: 'email', key: 'email' },
                {
                  title: '系统角色',
                  dataIndex: 'role',
                  key: 'role',
                  width: 140,
                  render: (_role, record) => renderSystemRole(record)
                },
                ...(userPermissions.includes('分组显示') ? [
                  {
                    title: '分组',
                    dataIndex: 'groups',
                    key: 'groups',
                    render: (groups) => renderGroupTags(groups)
                  }
                ] : []),
                { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (text) => {
                  if (!text) return '-';
                  const d = new Date(text);
                  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
                } },
                { title: '操作', key: 'action', render: (_, record) => (
                  <span>
                    <Button type="link" onClick={() => handleEditUser(record)}>编辑</Button>
                    <Button type="link" onClick={() => handleDeleteUser(record.id)}>删除</Button>
                    <Button type="link" onClick={() => handleResetPwd(record.id)}>重置密码</Button>
                  </span>
                ) }
              ]}
            />
            <Modal
              open={userModalVisible}
              onCancel={() => setUserModalVisible(false)}
              onOk={() => userForm.submit()}
              title={editingUser ? '编辑用户' : '添加用户'}
            >
              <Form form={userForm} onFinish={handleSaveUser}>
                <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
                  <Input disabled={!!editingUser} placeholder="编辑时不可修改" />
                </Form.Item>
                <Form.Item name="realName" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input placeholder="请输入真实姓名" />
                </Form.Item>
                <Form.Item name="email" label="邮箱" rules={[{ required: true, message: '请输入邮箱' }]}>
                  <Input placeholder="请输入邮箱地址" />
                </Form.Item>
                <Form.Item name="password" label="密码" rules={[{ required: !editingUser, message: '请输入密码' }]} hidden={!!editingUser}>
                  <Input.Password placeholder="编辑时不可修改密码" />
                </Form.Item>
                <Form.Item
                  name="role"
                  label="系统角色"
                  rules={[{ required: true, message: '请选择系统角色' }]}
                >
                  <Select placeholder="admin / manager / captain / leader / user">
                    {SYSTEM_ROLE_SELECT_OPTIONS.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
            </Modal>
          </div>
        )
      });
    }

    if (shouldShowPage('会员信息管理')) {
      memberTabs.push({
        key: 'member_info',
        label: '设备队员',
        children: (
          <div>
            <p style={{ color: '#666', marginBottom: 16 }}>这里管理定位设备/队员档案，不是登录账号。</p>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Input.Search
                placeholder="搜索会员名称或设备ID"
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
                onSearch={() => fetchMembers(1)}
                style={{ width: 260 }}
              />
              <Select
                allowClear
                placeholder="筛选组织"
                style={{ width: 180 }}
                value={memberOrgFilter || undefined}
                onChange={(value) => {
                  const org = value || '';
                  setMemberOrgFilter(org);
                  setMemberGroupFilter('');
                  fetchMembers(1, { org_name: org, group_name: '' });
                }}
                options={orgNames.map((name) => ({ value: name, label: name }))}
              />
              <Select
                allowClear
                placeholder="筛选小组"
                style={{ width: 160 }}
                value={memberGroupFilter || undefined}
                onChange={(value) => {
                  const group = value || '';
                  setMemberGroupFilter(group);
                  fetchMembers(1, { group_name: group });
                }}
                options={groupNamesForOrg.map((name) => ({ value: name, label: name }))}
              />
              <Button type="primary" onClick={() => setMemberModalVisible(true)}>添加会员</Button>
              <Button onClick={() => fetchMembers(1)}>刷新</Button>
              <Button onClick={handleAddOrg}>新增组织</Button>
            </div>
            {orgNames.length > 0 && (
              <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 4 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>组织 / 小组</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {orgNames.map((org) => (
                    <div key={org} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <OrgGroupActionPopover
                        items={[
                          { key: 'rename', label: '重命名', onClick: () => handleRenameOrg(org) },
                          { key: 'addGroup', label: '加小组', onClick: () => handleAddGroupOrg(org) },
                          { type: 'divider', key: 'd1' },
                          { key: 'delete', label: '删除组织', danger: true, onClick: () => handleDeleteOrgModal(org) }
                        ]}
                      >
                        {/* 一级：组织 — 深色底白字 */}
                        <span
                          role="button"
                          tabIndex={0}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            padding: '4px 12px',
                            borderRadius: 6,
                            background: '#1f4e79',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 14,
                            letterSpacing: '0.02em',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                            display: 'inline-block'
                          }}
                        >
                          {org}
                        </span>
                      </OrgGroupActionPopover>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                        <Switch
                          size="small"
                          checked={!!orgInterconnect?.[org]}
                          onChange={(checked) => handleToggleOrgInterconnect?.(org, checked)}
                        />
                        <span style={{ fontSize: 12, color: orgInterconnect?.[org] ? '#1f4e79' : '#8c8c8c' }}>
                          {orgInterconnect?.[org] ? '通联' : '仅本组织'}
                        </span>
                      </span>
                      {(organizations[org] || []).map((group) => (
                        <OrgGroupActionPopover
                          key={`${org}-${group}`}
                          items={[
                            { key: 'rename', label: '重命名', onClick: () => handleRenameGroupOrg(org, group) },
                            { type: 'divider', key: 'd1' },
                            { key: 'delete', label: '删除小组', danger: true, onClick: () => handleDeleteGroupOrg(org, group) }
                          ]}
                        >
                          {/* 二级：小组 — 浅蓝胶囊 */}
                          <span
                            role="button"
                            tabIndex={0}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              background: '#f0f5fa',
                              color: '#3a5a7a',
                              border: '1px solid #c5d4e3',
                              borderRadius: 14,
                              padding: '2px 10px',
                              fontWeight: 500,
                              fontSize: 13,
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            {group}
                          </span>
                        </OrgGroupActionPopover>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {memberMsg && <div style={{ color: memberMsg.includes('成功') ? 'green' : 'red', marginBottom: 16 }}>{memberMsg}</div>}

            <Table
              dataSource={members}
              rowKey={(record) => record.device_unique_id || String(record.id)}
              rowSelection={{ selectedRowKeys: selectedMemberKeys, onChange: setSelectedMemberKeys }}
              pagination={{
                current: memberPagination.current,
                pageSize: memberPagination.pageSize,
                total: memberPagination.total,
                onChange: (page) => fetchMembers(page),
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onShowSizeChange: (current, size) => {
                  setMemberPagination(prev => ({ ...prev, pageSize: size }));
                  fetchMembers(1);
                }
              }}
              columns={[
                { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
                { title: '设备ID', dataIndex: 'device_unique_id', key: 'device_unique_id', width: 120, ellipsis: true },
                { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
                { title: '小组', dataIndex: 'group_name', key: 'group_name', width: 100 },
                { title: '组织', dataIndex: 'org_name', key: 'org_name', width: 100 },
                { title: '最后在线', dataIndex: 'last_seen', key: 'last_seen', width: 150, render: (text) => {
                  if (!text) return '-';
                  const d = new Date(text);
                  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
                } },
                { title: '位置记录数', dataIndex: 'location_count', key: 'location_count', width: 100 },
                { title: '电池电量', dataIndex: 'battery_level', key: 'battery_level', width: 80, render: (level) => {
                  if (level === null || level === undefined) return '-';
                  return `${level}%`;
                } },
                { title: '操作', key: 'action', width: 200, render: (_, record) => (
                  <span>
                    <Button type="link" size="small" onClick={() => handleEditMember(record)}>编辑</Button>
                    <Button type="link" size="small" danger onClick={() => handleDeleteMember(record.device_unique_id)}>删除</Button>
                    <Button type="link" size="small" onClick={() => {
                      setExportModal?.({
                        visible: true,
                        ids: [record.device_unique_id],
                        start: '',
                        end: '',
                        format: 'kml'
                      });
                    }}>导出轨迹</Button>
                  </span>
                ) }
              ]}
            />

            {selectedMemberKeys.length > 0 && (
              <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>批量操作（已选择 {selectedMemberKeys.length} 项）:</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button size="small" onClick={handleBatchDeleteMember}>批量删除</Button>
                  <Button size="small" onClick={handleBatchDeleteLocation}>批量删除位置记录</Button>
                  <Button size="small" onClick={() => setExportModal?.({
                    visible: true,
                    ids: [...selectedMemberKeys],
                    start: '',
                    end: '',
                    format: 'kml'
                  })}>导出轨迹</Button>
                  <Button size="small" onClick={openBatchIconModal}>批量设置图标</Button>
                </div>
              </div>
            )}

            <Modal
              open={!!exportModal?.visible}
              title="导出轨迹"
              okText="开始导出"
              onCancel={() => setExportModal?.({ visible: false, ids: [], start: '', end: '', format: 'kml' })}
              onOk={handleExportTrack}
            >
              <p style={{ marginBottom: 12 }}>将导出 {exportModal?.ids?.length || 0} 个队员的位置轨迹。</p>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}>开始时间（可选）</div>
                <Input
                  type="datetime-local"
                  value={exportModal?.start || ''}
                  onChange={(e) => setExportModal?.(prev => ({ ...prev, start: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}>结束时间（可选）</div>
                <Input
                  type="datetime-local"
                  value={exportModal?.end || ''}
                  onChange={(e) => setExportModal?.(prev => ({ ...prev, end: e.target.value }))}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4 }}>格式</div>
                <Select
                  style={{ width: '100%' }}
                  value={exportModal?.format || 'kml'}
                  onChange={(format) => setExportModal?.(prev => ({ ...prev, format }))}
                  options={[
                    { value: 'kml', label: 'KML' },
                    { value: 'gpx', label: 'GPX' },
                    { value: 'csv', label: 'CSV' },
                    { value: 'json', label: 'JSON' }
                  ]}
                />
              </div>
            </Modal>

            <Modal
              open={!!batchIconModal?.visible}
              title={`批量设置图标（已选 ${selectedMemberKeys.length} 项）`}
              okText="确认设置"
              onCancel={() => setBatchIconModal?.({ visible: false, icon: 'water_drop' })}
              onOk={submitBatchSetIcon}
            >
              <div style={{ marginBottom: 8 }}>选择图标</div>
              <Select
                style={{ width: '100%' }}
                value={batchIconModal?.icon || 'water_drop'}
                onChange={(icon) => setBatchIconModal?.(prev => ({ ...prev, icon }))}
                options={MEMBER_ICON_OPTIONS.map((icon) => ({ value: icon, label: icon }))}
              />
            </Modal>

            <Modal
              open={memberModalVisible}
              onCancel={() => setMemberModalVisible(false)}
              onOk={() => memberForm.submit()}
              title="添加会员"
              width={600}
            >
              <Form form={memberForm} onFinish={handleSaveMember}>
                <Form.Item name="device_unique_id" label="设备ID" rules={[{ required: true, message: '请输入设备ID' }]}>
                  <Input placeholder="请输入设备唯一标识" />
                </Form.Item>
                <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input placeholder="请输入会员姓名" />
                </Form.Item>
                <Form.Item name="group_name" label="小组">
                  <Input placeholder="请输入所属小组" />
                </Form.Item>
                <Form.Item name="org_name" label="组织">
                  <Input placeholder="请输入所属组织" />
                </Form.Item>
                <Form.Item name="device_model" label="设备型号">
                  <Input placeholder="请输入设备型号" />
                </Form.Item>
              </Form>
            </Modal>

            <Modal
              open={memberEditModalVisible}
              onCancel={() => setMemberEditModalVisible(false)}
              onOk={() => memberEditForm.submit()}
              title="编辑会员信息"
              width={600}
            >
              <Form form={memberEditForm} onFinish={handleUpdateMember}>
                <Form.Item name="device_unique_id" label="设备ID">
                  <Input disabled />
                </Form.Item>
                <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input placeholder="请输入会员姓名" />
                </Form.Item>
                <Form.Item name="group_name" label="小组">
                  <Input placeholder="请输入所属小组" />
                </Form.Item>
                <Form.Item name="org_name" label="组织">
                  <Input placeholder="请输入所属组织" />
                </Form.Item>
              </Form>
            </Modal>
          </div>
        )
      });
    }

    if (memberTabs.length > 0) {
      tabs.push({
        key: 'members',
        label: '账号与队员',
        children: <Tabs defaultActiveKey={memberTabs[0].key} items={memberTabs} />
      });
    }
  }

  return tabs[0] || null;
}
